import React, { useEffect, useState } from 'react';
import Loader from './Loader';
import { useT } from '../i18n';
import './InvoiceDetailsModal.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

// Helper to format fuel types for display.
// Non-React module-level helper: read the stored language directly.
const formatFuelType = (fuelKey) => {
  const en = localStorage.getItem('k360-lang') === 'en';
  const fuelLabels = {
    'diesel_mobile': en ? 'Diesel' : 'Dizel',
    'gasoline': en ? 'Petrol' : 'Benzinë',
    'lpg': 'LPG',
    'natural_gas': en ? 'Natural gas' : 'Gaz natyror',
    'diesel_bus_distance': en ? 'Bus (Diesel)' : 'Autobus (Dizel)',
    'fuel_vehicle': en ? 'Fuel vehicle' : 'Automjet me karburant',
    'electric_vehicle': en ? 'Electric vehicle' : 'Automjet elektrik'
  };
  return fuelLabels[fuelKey] || fuelKey || 'N/A';
};

// Helper to get modal title based on scope/type.
// Non-React module-level helper: read the stored language directly.
const getModalTitle = (scope, method) => {
  const en = localStorage.getItem('k360-lang') === 'en';
  if (scope === 'scope1') {
    if (method?.toLowerCase().includes('mobile')) return en ? 'MOBILE COMBUSTION' : 'DJEGJA MOBILE';
    if (method?.toLowerCase().includes('stationary') || method?.toLowerCase().includes('stacionar')) return en ? 'STATIONARY COMBUSTION' : 'DJEGJA STACIONARE';
    if (method?.toLowerCase().includes('process')) return en ? 'INDUSTRIAL PROCESSES' : 'PROCESET INDUSTRIALE';
    return en ? 'SCOPE 1 - EMISSIONS' : 'SCOPE 1 - EMETIMET';
  }
  if (scope === 'scope3') return en ? 'SCOPE 3 - EMISSIONS' : 'SCOPE 3 - EMETIMET';
  return en ? 'ELECTRICITY INVOICE' : 'FATURA E RRYMËS';
};

export const InvoiceDetailsModal = ({ isOpen, onClose, invoiceData }) => {
  const { t } = useT();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState('pdf');
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Safely read file path for preview loading even before early return
  const filePath = invoiceData?.file_path;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Fetch file as blob to avoid iframe cross-origin issues
  useEffect(() => {
    let objectUrl = null;
    let aborted = false;
    setPreviewError('');
    setPreviewUrl(null);
    
    if (!filePath) {
      setLoadingPreview(false);
      return;
    }
    
    setLoadingPreview(true);

    const loadPreview = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/${filePath}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.status === 404) {
          // File no longer on the server (uploads made before persistent storage was attached)
          if (!aborted) {
            setPreviewError(t('invdet.fileGone'));
            setLoadingPreview(false);
          }
          return;
        }
        if (!res.ok) throw new Error(`Failed to load file: ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (aborted) return;
        setPreviewType(blob.type.startsWith('image/') ? 'image' : 'pdf');
        setPreviewUrl(objectUrl);
        setLoadingPreview(false);
      } catch (err) {
        if (!aborted) {
          setPreviewError(t('invdet.previewFailed'));
          setLoadingPreview(false);
        }
        console.error('Preview load error:', err);
      }
    };

    loadPreview();
    return () => {
      aborted = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [filePath]);

  if (!isOpen || !invoiceData) return null;

  const {
    period,
    supplier = 'KESCO',
    method = 'Location-based',
    registrations = { A1: 0, A2: 0, B1: 0, B2: 0 },
    consumption = { current: 0, previous: 0 },
    emissions = { kwh: 0, tco2e: 0 },
    file_name,
    scope = 'scope2',
    source,
    // Scope 1 specific fields
    fuel_type,
    quantity,
    unit
  } = invoiceData;

  const isScope1 = scope === 'scope1';
  const isScope2 = scope === 'scope2' || scope?.includes('elektriciteti');
  const isScope3 = scope === 'scope3';
  const hasFile = !!filePath;

  // Render Scope 1 content (Mobile/Stationary combustion)
  const renderScope1Content = () => (
    <div className="invoice-modal-left invoice-modal-full">
      {/* Info Row */}
      <div className="invoice-info-row">
        <div className="invoice-info-item">
          <span className="invoice-info-label">{t('invdet.typeLabel')}</span>
          <span className="invoice-info-value">{supplier || t('invdet.vehicle')}</span>
        </div>
        <div className="invoice-info-item">
          <span className="invoice-info-label">{t('invdet.methodLabelColon')}</span>
          <span className="invoice-info-value">{method}</span>
        </div>
      </div>

      {/* Fuel Details Section */}
      <div className="invoice-section">
        <h3 className="invoice-section-title">{t('invdet.fuelDetails')}</h3>
        <div className="invoice-fuel-details">
          <div className="invoice-fuel-item">
            <span className="invoice-fuel-label">{t('invdet.fuelType')}</span>
            <span className="invoice-fuel-value">{formatFuelType(fuel_type)}</span>
          </div>
          <div className="invoice-fuel-item">
            <span className="invoice-fuel-label">{t('invdet.quantity')}</span>
            <span className="invoice-fuel-value">{quantity || 0} <span className="invoice-fuel-unit">{unit || 'L'}</span></span>
          </div>
          <div className="invoice-fuel-item">
            <span className="invoice-fuel-label">{t('invdet.period')}</span>
            <span className="invoice-fuel-value">{period || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Emissions Summary */}
      <div className="invoice-section">
        <h3 className="invoice-section-title">{t('invdet.emissions')}</h3>
        <div className="invoice-emissions-card">
          <div className="invoice-emissions-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div className="invoice-emissions-data">
            <span className="invoice-emissions-value">{(emissions.tco2e || 0).toFixed(3)}</span>
            <span className="invoice-emissions-unit">t CO₂e</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Scope 2 content (Electricity)
  const renderScope2Content = () => (
    <>
      <div className="invoice-modal-left">
        {/* Supplier Info */}
        <div className="invoice-info-row">
          <div className="invoice-info-item">
            <span className="invoice-info-label">{t('invdet.supplierLabel')}</span>
            <span className="invoice-info-value">{supplier}</span>
          </div>
          <div className="invoice-info-item">
            <span className="invoice-info-label">{t('invdet.methodLabel')}</span>
            <span className="invoice-info-value">{method}</span>
          </div>
        </div>

        {/* Regjistrat Section */}
        <div className="invoice-section">
          <h3 className="invoice-section-title">{t('invdet.registers')}</h3>
          <div className="invoice-registrations">
            <div className="invoice-reg-item">
              <span className="invoice-reg-label">A1</span>
              <div className="invoice-reg-value">
                {registrations.A1} <span className="invoice-reg-unit">kWh</span>
              </div>
            </div>
            <div className="invoice-reg-item">
              <span className="invoice-reg-label">A2</span>
              <div className="invoice-reg-value">
                {registrations.A2} <span className="invoice-reg-unit">kWh</span>
              </div>
            </div>
            <div className="invoice-reg-item">
              <span className="invoice-reg-label">B1</span>
              <div className="invoice-reg-value">
                {registrations.B1} <span className="invoice-reg-unit">kWh</span>
              </div>
            </div>
            <div className="invoice-reg-item">
              <span className="invoice-reg-label">B2</span>
              <div className="invoice-reg-value">
                {registrations.B2} <span className="invoice-reg-unit">kWh</span>
              </div>
            </div>
          </div>
        </div>

        {/* Krahasimi Section (Comparison) */}
        {(() => {
          const prevValue = consumption.previous || 0;
          const currValue = consumption.current || 0;
          const maxValue = Math.max(prevValue, currValue) || 1;
          
          const maxHeight = 140;
          const minHeight = 100;
          const noDataHeight = 80;
          
          const bothZero = prevValue === 0 && currValue === 0;
          const prevHeight = bothZero ? minHeight : (prevValue > 0 ? Math.max(minHeight, (prevValue / maxValue) * maxHeight) : noDataHeight);
          const currHeight = bothZero ? minHeight : (currValue > 0 ? Math.max(minHeight, (currValue / maxValue) * maxHeight) : noDataHeight);
          
          const formatValue = (val) => {
            if (val >= 1000) return Math.round(val).toLocaleString();
            return val.toString();
          };
          
          return (
            <div className="invoice-section">
              <h3 className="invoice-section-title">{t('invdet.comparison')}</h3>
              <div className="invoice-comparison">
                {/* Previous Month Box - proportional height */}
                <div 
                  className="invoice-comparison-box invoice-comparison-dark"
                  style={{ height: `${prevHeight}px` }}
                >
                  {prevValue > 0 ? (
                    <>
                      <div className="invoice-comparison-value">{formatValue(prevValue)}</div>
                      <div className="invoice-comparison-unit">kWh</div>
                      <div className="invoice-comparison-label">{t('invdet.prevMonth')}</div>
                    </>
                  ) : (
                    <div className="invoice-comparison-no-data">{t('invdet.noDataLine1')}<br/>{t('invdet.noDataLine2')}</div>
                  )}
                </div>
                {/* Current Month Box - proportional height */}
                <div 
                  className="invoice-comparison-box invoice-comparison-cyan"
                  style={{ height: `${currHeight}px` }}
                >
                  <div className="invoice-comparison-value">{formatValue(currValue)}</div>
                  <div className="invoice-comparison-unit">kWh</div>
                  <div className="invoice-comparison-label">{t('invdet.currMonth')}</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Bottom Metrics */}
        <div className="invoice-metrics">
          <div className="invoice-metric">
            <div className="invoice-metric-label">
              <span className="invoice-metric-arrow">↑</span>
              {t('invdet.electricityUse')}
            </div>
            <div className="invoice-metric-value-row">
              <span className="invoice-metric-value">{emissions.kwh}</span>
              <span className="invoice-metric-unit">kWh</span>
            </div>
          </div>
          <div className="invoice-metric">
            <div className="invoice-metric-label">
              <span className="invoice-metric-arrow">↑</span>
              {t('invdet.carbonEmissions')}
            </div>
            <div className="invoice-metric-value-row">
              <span className="invoice-metric-value">{(emissions.tco2e || 0).toFixed(3)}</span>
              <span className="invoice-metric-unit">t CO₂e</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - PDF Preview */}
      <div className="invoice-modal-right">
        <div className="invoice-pdf-container">
          <div className="invoice-pdf-viewer-wrapper">
            {loadingPreview ? (
              <div className="invoice-loader">
                <Loader fullscreen={false} />
                <span>{t('invdet.loadingDoc')}</span>
              </div>
            ) : previewUrl ? (
              previewType === 'image' ? (
                <img
                  src={previewUrl}
                  alt={file_name || 'Invoice'}
                  className="invoice-image-viewer"
                  onLoad={() => setLoadingPreview(false)}
                  onError={() => setLoadingPreview(false)}
                />
              ) : (
                <iframe
                  src={previewUrl}
                  className="invoice-pdf-viewer"
                  title="Invoice PDF"
                  onLoad={() => setLoadingPreview(false)}
                  onError={() => setLoadingPreview(false)}
                />
              )
            ) : (
              <div className="invoice-pdf-placeholder">
                <span>📄</span>
                <p>{previewError || t('invdet.noAttachment')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  // Render Scope 3 content
  const renderScope3Content = () => (
    <div className="invoice-modal-left invoice-modal-full">
      {/* Info Row */}
      <div className="invoice-info-row">
        <div className="invoice-info-item">
          <span className="invoice-info-label">{t('invdet.categoryLabel')}</span>
          <span className="invoice-info-value">{supplier || 'Scope 3'}</span>
        </div>
        <div className="invoice-info-item">
          <span className="invoice-info-label">{t('invdet.typeLabel')}</span>
          <span className="invoice-info-value">{method}</span>
        </div>
      </div>

      {/* Activity Details */}
      <div className="invoice-section">
        <h3 className="invoice-section-title">{t('invdet.activityDetails')}</h3>
        <div className="invoice-fuel-details">
          <div className="invoice-fuel-item">
            <span className="invoice-fuel-label">{t('invdet.quantity')}</span>
            <span className="invoice-fuel-value">{quantity || 0} <span className="invoice-fuel-unit">{unit || 'km'}</span></span>
          </div>
          <div className="invoice-fuel-item">
            <span className="invoice-fuel-label">{t('invdet.period')}</span>
            <span className="invoice-fuel-value">{period || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Emissions Summary */}
      <div className="invoice-section">
        <h3 className="invoice-section-title">{t('invdet.emissions')}</h3>
        <div className="invoice-emissions-card">
          <div className="invoice-emissions-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div className="invoice-emissions-data">
            <span className="invoice-emissions-value">{(emissions.tco2e || 0).toFixed(3)}</span>
            <span className="invoice-emissions-unit">t CO₂e</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="invoice-modal-overlay" onClick={handleBackdropClick}>
      <div className={`invoice-modal-container ${!hasFile && !isScope2 ? 'invoice-modal-narrow' : ''}`}>
        {/* Close Button */}
        <button className="invoice-modal-close" onClick={onClose}>
          ×
        </button>

        {/* Header */}
        <div className="invoice-modal-header">
          <h2>{getModalTitle(scope, method)}</h2>
        </div>

        {/* Source info banner */}
        {source && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            margin: '0 24px 12px',
            borderRadius: '8px',
            fontSize: '13px',
            background: source === 'ocr' ? '#e0fff8' : '#f3f4f6',
            color: source === 'ocr' ? '#0d7d6c' : '#6b7280'
          }}>
            <span style={{ fontSize: '16px' }}>{source === 'ocr' ? '📄' : '✏️'}</span>
            {source === 'ocr'
              ? t('invdet.sourceOcr')
              : t('invdet.sourceManual')}
          </div>
        )}

        {/* Content - Render based on scope */}
        <div className="invoice-modal-content">
          {isScope1 && renderScope1Content()}
          {isScope2 && renderScope2Content()}
          {isScope3 && renderScope3Content()}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsModal;
